import React, { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate } from "react-router-dom";

type UnsavedChangesPromptProps = {
  when: boolean;
};

// POL-021 (Q-027 approved): in addition to the original beforeunload guard and
// same-origin anchor-click interception, browser Back/Forward is now blocked
// via a history-sentinel + popstate listener. react-router-dom 7.1.5 exports
// useBlocker but it requires a data router (createBrowserRouter); the app
// mounts a plain <BrowserRouter> (src/index.tsx), so the sentinel approach is
// used instead. Known accepted limitations: a history-menu long-press jump of
// >= 2 entries escapes the single sentinel, and programmatic navigate()
// redirects (save-success flows) remain un-blocked — identical to before,
// which is desired.

type PendingNavigation =
  | { kind: "href"; href: string }
  | { kind: "back" };

type SentinelHistoryState = { cmpUnsavedSentinel?: boolean } | null | undefined;

const armSentinel = () => {
  // A duplicate same-URL entry. pushState does not notify BrowserRouter (v7
  // listens only to popstate), so this causes no render churn.
  window.history.pushState(
    { cmpUnsavedSentinel: true },
    "",
    window.location.href
  );
};

const UnsavedChangesPrompt = ({ when }: UnsavedChangesPromptProps) => {
  const [pendingNavigation, setPendingNavigation] =
    useState<PendingNavigation | null>(null);
  const allowNextNavigationRef = useRef(false);
  const swallowNextPopRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!when) {
      setPendingNavigation(null);
      allowNextNavigationRef.current = false;
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (allowNextNavigationRef.current) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [when]);

  useEffect(() => {
    if (!when) {
      return;
    }

    const handleDocumentClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const anchor = target?.closest("a[href]") as HTMLAnchorElement | null;

      if (!anchor || anchor.target === "_blank" || anchor.hasAttribute("download")) {
        return;
      }

      const destination = new URL(anchor.href, window.location.href);
      const current = new URL(window.location.href);
      const isSameDocumentHash =
        destination.origin === current.origin &&
        destination.pathname === current.pathname &&
        destination.search === current.search &&
        destination.hash &&
        destination.hash !== current.hash;

      if (destination.origin !== current.origin || isSameDocumentHash) {
        return;
      }

      if (destination.href === current.href) {
        return;
      }

      event.preventDefault();
      setPendingNavigation({ kind: "href", href: destination.href });
    };

    document.addEventListener("click", handleDocumentClick, true);

    return () => {
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, [when]);

  // POL-021: browser Back/Forward interception via a same-URL sentinel entry.
  useEffect(() => {
    if (!when) {
      return;
    }

    armSentinel();

    const handlePopState = () => {
      if (allowNextNavigationRef.current) {
        return;
      }

      if (swallowNextPopRef.current) {
        swallowNextPopRef.current = false;
        return;
      }

      // Re-arm immediately: pushing the same URL again also truncates forward
      // history, so Forward is blocked too. The popped-to entry has the
      // identical URL, so BrowserRouter's popstate handling is visually a
      // no-op.
      armSentinel();
      setPendingNavigation({ kind: "back" });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);

      // Disarm: collapse the sentinel so a post-save Back press is not a dead
      // click. The swallow flag makes the one-shot fallback listener below
      // ignore that programmatic pop (and reset the flag for any future
      // re-arm).
      const historyState = window.history.state as SentinelHistoryState;
      if (historyState?.cmpUnsavedSentinel) {
        swallowNextPopRef.current = true;
        const handleSwallowFallback = () => {
          swallowNextPopRef.current = false;
          window.removeEventListener("popstate", handleSwallowFallback);
        };
        window.addEventListener("popstate", handleSwallowFallback);
        window.history.back();
      }
    };
  }, [when]);

  if (!pendingNavigation) {
    return null;
  }

  const handleLeave = () => {
    if (pendingNavigation.kind === "back") {
      setPendingNavigation(null);
      allowNextNavigationRef.current = true;
      // Skips the re-armed sentinel + the original entry, landing where the
      // user's Back intended. BrowserRouter handles the resulting popstate
      // normally; if the target is outside the SPA, the beforeunload
      // suppression above lets it through.
      window.history.go(-2);
      return;
    }

    const href = pendingNavigation.href;
    const destination = new URL(href, window.location.href);

    setPendingNavigation(null);

    if (destination.origin === window.location.origin) {
      navigate(`${destination.pathname}${destination.search}${destination.hash}`);
      return;
    }

    allowNextNavigationRef.current = true;
    window.location.assign(href);
  };

  const handleStay = () => setPendingNavigation(null);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4">
      <div
        className="relative w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 text-center shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="unsaved-changes-title"
      >
        <button
          type="button"
          onClick={handleStay}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white transition hover:bg-red-700"
          aria-label="Close unsaved changes warning"
        >
          <CloseIcon fontSize="small" />
        </button>
        <div className="mx-auto mt-2 flex h-16 w-16 items-center justify-center rounded-full bg-cpaGold text-white">
          <InfoOutlinedIcon sx={{ fontSize: 40 }} />
        </div>
        <h2
          id="unsaved-changes-title"
          className="mt-5 text-lg font-bold uppercase tracking-wide text-gray-950"
        >
          UNSAVED CHANGES
        </h2>
        <p className="mt-4 text-sm leading-6 text-gray-950">
          You have unsaved changes on this page.
        </p>
        <p className="mt-2 text-sm leading-6 text-gray-950">
          If you leave now, your changes will be lost.
        </p>
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={handleStay}
            className="rounded-lg border border-cpaGold bg-cpaGold px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-500"
          >
            Save Changes
          </button>
          <button
            type="button"
            onClick={handleLeave}
            className="rounded-lg border border-neutral-800 bg-neutral-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-neutral-900"
          >
            Leave Without Saving
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnsavedChangesPrompt;
